import { useCallback, useEffect, useRef, useState } from 'react';

import { changeCardDryRun } from '../../services/changeCard';
import { apiErrorCode, apiErrorMessage } from '../../utils/apiErrors';

export const DRY_RUN_DELAY_MS = 500;

const IDLE = Object.freeze({ data: null, problems: [], error: null, loading: false, key: null });

/**
 * The Change card dry run, re-asked on every change to the mapping (KPR-707).
 *
 * `body` is the request (`changeCardModel.dryRunBody`), or null while there is
 * nothing to ask (no target card yet). A change waits `delay` ms for the next
 * one, so a run of drops costs one request; an answer to an older body is
 * dropped (a generation guard, and the older request is aborted).
 *
 * - `data` is the last answer that landed, kept while the next is on its way
 *   and when the next is refused, so the counts never blank between changes.
 *   It is cleared when the body becomes null (another target, no counts).
 * - `problems` is a 400 `change_card_mapping_invalid`'s list, each naming a
 *   target element; `error` is any other refusal, as `{ code, message, data }`
 *   (`data` the refusal's body: a 409 `change_card_blocked_by_lookups`
 *   carries `lookups`, `hiddenItems` and `items`).
 * - `fresh` is true when `data` answers this very body and nothing is on its
 *   way or refused: the only answer whose token the review confirms with.
 * - `loading` is true from the change until its answer lands.
 */
export default function useChangeCardDryRun(collectionId, body, { delay = DRY_RUN_DELAY_MS } = {}) {
	const [state, setState] = useState(IDLE);
	const [tick, setTick] = useState(0);
	const generationRef = useRef(0);
	const key = body && collectionId ? JSON.stringify(body) : null;

	useEffect(() => {
		const generation = ++generationRef.current;
		if (!key) {
			setState(IDLE);
			return undefined;
		}
		setState(prev => ({ ...prev, loading: true }));
		const controller = typeof AbortController === 'function' ? new AbortController() : null;
		const timer = setTimeout(() => {
			changeCardDryRun(collectionId, JSON.parse(key), { signal: controller?.signal })
				.then(data => {
					if (generation !== generationRef.current) return;
					setState({ data, problems: [], error: null, loading: false, key });
				})
				.catch(err => {
					if (generation !== generationRef.current) return;
					const code = apiErrorCode(err);
					if (code === 'change_card_mapping_invalid') {
						const problems = Array.isArray(err.response?.data?.problems) ? err.response.data.problems : [];
						setState(prev => ({ ...prev, problems, error: null, loading: false, key }));
						return;
					}
					setState(prev => ({
						...prev,
						problems: [],
						error: {
							code,
							message: apiErrorMessage(err, 'Could not work out the change. Please try again.'),
							data: err?.response?.data || null,
						},
						loading: false,
						key,
					}));
				});
		}, delay);
		return () => {
			clearTimeout(timer);
			controller?.abort();
		};
	}, [collectionId, key, delay, tick]);

	const retry = useCallback(() => setTick(value => value + 1), []);

	// The last answer is the one for this very body, and nothing is on its
	// way: what the review may confirm with (KPR-709).
	const fresh = !!key && state.key === key && !!state.data && !state.loading && !state.error && !state.problems.length;

	return { ...state, fresh, retry };
}
